import { logNoLiquidity } from "@src/utils/logCustom"
import { Err, Ok, type Result } from "@thames/monads"
import { formatUnits } from "viem"
import type { BaseTokenInfo } from "../../../types/base"

export type AggregatedQuoteErr = { reason: "NO_QUOTES" }

export type QuoteExactInParams = {
  tokenIn: string
  tokenOut: string
  amountIn: bigint
}

/**
 * Validates liquidity for multiple token swaps in parallel via the 1-Click API.
 *
 * Used by the OTC taker flow: when a taker fills an order, they may not hold
 * the exact tokens the maker wants. `fillWithMinimalExchanges` computes
 * intermediate conversion steps (e.g. USDC.base → USDC.eth), and each step
 * where fromToken !== toToken needs a liquidity check. If any step has no
 * liquidity, the whole fill fails.
 *
 * The OTC price itself is pre-agreed between maker and taker — these checks
 * only validate that the intermediate token conversions are possible.
 *
 * `resolveToken` maps a `defuseAssetId` to a `BaseTokenInfo` so a no-liquidity
 * Sentry event can be emitted with human-readable symbol/chain — restoring the
 * signal that existed before the 1-Click migration.
 */
export async function validateLiquidity(
  swapParams: QuoteExactInParams[],
  resolveToken: (defuseAssetId: string) => BaseTokenInfo | undefined
): Promise<Result<true, AggregatedQuoteErr>> {
  // Lazy import: 1cs.ts validates ONE_CLICK_URL at module level,
  // which crashes in test environments where the env var is not set.
  const { getInternalQuote } = await import("../../machines/1cs")

  const results = await Promise.all(
    swapParams.map(async ({ tokenIn, tokenOut, amountIn }) => {
      try {
        await getInternalQuote({
          originAsset: tokenIn,
          destinationAsset: tokenOut,
          amount: amountIn.toString(),
        })
        return Ok<true, AggregatedQuoteErr>(true)
      } catch {
        const tokenInInfo = resolveToken(tokenIn)
        const tokenOutInfo = resolveToken(tokenOut)
        logNoLiquidity({
          tokenIn: tokenInInfo,
          tokenOut: tokenOutInfo,
          amount: formatUnits(amountIn, tokenInInfo?.decimals ?? 0),
          tags: { source: "otc-taker-liquidity-validation" },
        })
        return Err<true, AggregatedQuoteErr>({ reason: "NO_QUOTES" })
      }
    })
  )

  const err = results.find((r) => r.isErr())
  if (err) {
    return Err(err.unwrapErr())
  }

  return Ok(true)
}

/**
 * Returns the maximum of minDeadlineMs and timeEstimateMs as an ISO string.
 * @param minDeadlineMs - The minimum deadline in milliseconds.
 * @param timeEstimateMs - The time estimate in milliseconds. Optional.
 * @returns ISO string of the deadline (current time + the greater of the two values).
 * @example
 * getMinDeadlineMs(600000) // "2026-01-01T12:10:00.000Z" (10 min from now)
 * getMinDeadlineMs(600000, 10000) // "2026-01-01T12:10:00.000Z" (10 min from now)
 * getMinDeadlineMs(600000, 1260000) // "2026-01-01T12:21:00.000Z" (21 min from now)
 */
export function getMinDeadlineMs(
  minDeadlineMs: number,
  timeEstimateMs?: number
): string {
  const deadlineMs = Math.max(minDeadlineMs, timeEstimateMs ?? 0)
  return new Date(Date.now() + deadlineMs).toISOString()
}
